import React from 'react';

import { StyleSheet, Text, View } from '@react-pdf/renderer';

// PRST-134 — table primitives for the Earnings History PDF.
//
// react-pdf has no table element; a table is Views in a row with explicit widths. Widths are
// POINTS, not percentages: the content box is 468pt (LETTER 612 minus two 1-inch margins) and
// percentage columns round independently, leaving a visible hairline gap at the right edge.
//
// This file styles with StyleSheet.create rather than the repo's scoped CSS because react-pdf
// paints to a canvas and never touches the DOM — scoped CSS and var(--primary) do not reach it.
// Same carve-out as QuoteEngine/GenerateQuote/quoteDocument.js and AIReview/evaluationDocument.js.

export const CONTENT_WIDTH = 468;

// Ticket: "Use bold black text and light grey background (#D7D7D7) for column headers."
export const GREY_HEADER = '#D7D7D7';

// react-pdf 1.6.11 DISCARDS the font descriptor for the standard PDF fonts, so `fontWeight: 'bold'`
// is silently a no-op (getFont() returns null for anything in its standardFonts list). Bold has to
// be selected as a font family. Every bold in this report goes through these two constants.
export const FONT = 'Helvetica';
export const FONT_BOLD = 'Helvetica-Bold';

export const tableStyles = StyleSheet.create({
  // No gridlines. The legacy PeopleReady report drew a fully boxed table; both Axure mockups
  // deliberately drop that and show a grey header band only, so cells carry no borders.
  table: { marginTop: 8 },
  row: { flexDirection: 'row' },
  headerRow: { flexDirection: 'row', backgroundColor: GREY_HEADER },
  cell: {
    paddingVertical: 3,
    paddingHorizontal: 4,
    fontSize: 8,
  },
  headerCell: { fontFamily: FONT_BOLD, color: '#000000' },
  numeric: { textAlign: 'right' },
  // Small blank gap below a group's total line, separating it from the next group.
  groupGap: { marginBottom: 7 },
  totalCell: { fontFamily: FONT_BOLD },
});

// Nulls are filtered out of the style array rather than passed through: react-pdf merges the
// array itself and a null entry is not something 1.6.11 documents as safe.
export const PdfCell = ({ column, children, header = false, total = false }) => (
  <View
    style={[
      tableStyles.cell,
      { width: column.width },
      column.numeric && tableStyles.numeric,
      header && tableStyles.headerCell,
      total && tableStyles.totalCell,
    ].filter(Boolean)}>
    <Text>{children === null || children === undefined ? '' : children}</Text>
  </View>
);

// wrap={false} keeps a single row from being sliced in half across a page break — the same guard
// AIReview/evaluationDocument.js puts on an exception block.
export const PdfHeaderRow = ({ columns }) => (
  <View style={tableStyles.headerRow} wrap={false}>
    {columns.map(column => (
      <PdfCell key={column.key} column={column} header>
        {column.header}
      </PdfCell>
    ))}
  </View>
);

export const PdfRow = ({ columns, row, total = false }) => (
  <View style={[tableStyles.row, row?.spacerAfter && tableStyles.groupGap].filter(Boolean)} wrap={false}>
    {columns.map(column => (
      <PdfCell key={column.key} column={column} total={total}>
        {column.render ? column.render(row) : row[column.key]}
      </PdfCell>
    ))}
  </View>
);

// Note: 1.6.11 has no "repeat the header when this table spills onto the next page". The only
// repeat mechanism is `fixed`, which repeats on every page of the whole document — wrong here.
// With one <Page> per month this only affects an unusually busy month; left as-is deliberately.
export const PdfTable = ({ columns, rows = [], totalRows = [] }) => (
  <View style={tableStyles.table}>
    <PdfHeaderRow columns={columns} />
    {rows.map((row, index) => (
      <PdfRow key={row.key || index} columns={columns} row={row} />
    ))}
    {totalRows.map((row, index) => (
      <PdfRow key={row.key || `total-${index}`} columns={columns} row={row} total />
    ))}
  </View>
);

export default PdfTable;
